/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

/**
 * The console's entire data layer: a thin typed wrapper over nexus REST. Deliberately owns what a
 * cache library would otherwise own — because monolith's live readers replace client caching in a
 * later milestone, surfaces consume narrow data-source functions from here and nothing else.
 *
 * Auth rides in cookies, not in script: nexus sets an HttpOnly access cookie (plus a path-scoped
 * refresh cookie) at login, so no token ever touches JS and the session survives reloads. The
 * non-HttpOnly XSRF-TOKEN cookie is echoed back as the X-XSRF-TOKEN header on every call — the
 * double-submit check nexus enforces for cookie-authenticated requests.
 *
 * Nexus failure envelope: `{ error: { code, message, fieldErrors: [...] } }`.
 * Success bodies are the resource itself.
 */

export type FieldError = { field: string; message: string };

export type ApiError = {
  code: string;
  message: string;
  fieldErrors: FieldError[];
  httpStatus: number;
  /** The whole failure body, for routes that answer a conflict with the current resource. */
  body?: unknown;
};

export type ApiResult<T> = { ok: true; value: T } | { ok: false; error: ApiError };

const REFRESH_PATH = "/v1/auth/refresh";
const XSRF_COOKIE = "XSRF-TOKEN";
const XSRF_HEADER = "X-XSRF-TOKEN";

export function readCookie(name: string): string | null {
  const match = document.cookie
    .split("; ")
    .find((part) => part.startsWith(name + "="));
  return match === undefined ? null : decodeURIComponent(match.slice(name.length + 1));
}

async function parseFailure(response: Response): Promise<ApiError> {
  let code = "HTTP_" + response.status;
  let message = response.statusText;
  let fieldErrors: FieldError[] = [];
  let body: unknown;
  try {
    body = await response.json();
    const envelope = body as {
      error?: { code?: string; message?: string; fieldErrors?: FieldError[] };
    };
    if (envelope.error) {
      code = envelope.error.code ?? code;
      message = envelope.error.message ?? message;
      fieldErrors = envelope.error.fieldErrors ?? [];
    }
  } catch {
    // non-JSON failure body — keep the HTTP-level facts
  }
  return { code, message, fieldErrors, httpStatus: response.status, body };
}

async function send(path: string, init: RequestInit): Promise<ApiResult<Response>> {
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  const xsrf = readCookie(XSRF_COOKIE);
  if (xsrf !== null) {
    headers.set(XSRF_HEADER, xsrf);
  }
  let response: Response;
  try {
    response = await fetch(path, { ...init, headers });
  } catch (cause) {
    return {
      ok: false,
      error: {
        code: "NETWORK",
        message: cause instanceof Error ? cause.message : "network failure",
        fieldErrors: [],
        httpStatus: 0,
      },
    };
  }
  if (!response.ok) {
    return { ok: false, error: await parseFailure(response) };
  }
  return { ok: true, value: response };
}

async function rawRequest<T>(path: string, init: RequestInit): Promise<ApiResult<T>> {
  const sent = await send(path, init);
  if (!sent.ok) {
    return sent;
  }
  if (sent.value.status === 204) {
    return { ok: true, value: undefined as T };
  }
  return { ok: true, value: (await sent.value.json()) as T };
}

async function rawBlob(path: string, init: RequestInit): Promise<ApiResult<Blob>> {
  const sent = await send(path, init);
  return sent.ok ? { ok: true, value: await sent.value.blob() } : sent;
}

/**
 * Single transparent refresh-and-retry on 401, shared by the JSON and binary paths. A second 401
 * falls through to the caller (the AuthProvider routes to login).
 */
async function withRefresh<T>(
  request: () => Promise<ApiResult<T>>,
): Promise<ApiResult<T>> {
  const first = await request();
  if (first.ok || first.error.httpStatus !== 401) {
    return first;
  }
  const refreshed = await tryRefresh();
  if (!refreshed) {
    return first;
  }
  return request();
}

/**
 * Rotates the session via the HttpOnly refresh cookie; new cookies arrive on the response.
 * Single-flighted: refresh tokens are single-use with reuse detection, so when several parallel
 * requests hit 401 together (an expired access cookie on a screen that fans out), they must all
 * await ONE rotation — concurrent rotations would present a consumed token, be refused as reuse,
 * and surface spurious auth errors mid-navigation.
 */
let refreshInFlight: Promise<boolean> | null = null;

function tryRefresh(): Promise<boolean> {
  refreshInFlight ??= rawRequest(REFRESH_PATH, { method: "POST" })
    .then((refreshed) => refreshed.ok)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

/**
 * Proactively rotates the session cookies so the access token's claims catch up with the
 * database (a just-granted role). Shares the single-flight with the 401 retry path.
 */
export function refreshSession(): Promise<boolean> {
  return tryRefresh();
}

/**
 * Authenticated request with a single transparent refresh-and-retry on 401. A second 401 falls
 * through to the caller (the AuthProvider routes to login).
 */
export function api<T>(path: string, init: RequestInit = {}): Promise<ApiResult<T>> {
  return withRefresh(() => rawRequest<T>(path, init));
}

/**
 * Authenticated binary GET (a fax PDF) with the same refresh-once behavior. The caller owns the
 * Blob's lifetime: an object URL made from it must be revoked, and the request should be aborted
 * via {@code init.signal} when its screen unmounts.
 */
export function apiBlob(path: string, init: RequestInit = {}): Promise<ApiResult<Blob>> {
  return withRefresh(() => rawBlob(path, init));
}

/** Request without the refresh-retry dance (login, OTP, logout). */
export function apiPublic<T>(path: string, init: RequestInit = {}): Promise<ApiResult<T>> {
  return rawRequest<T>(path, init);
}

export function post(body: unknown): RequestInit {
  return { method: "POST", body: JSON.stringify(body) };
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * State-mutating nexus writes require an Idempotency-Key, one per user action. A caller that may
 * retry the action passes the key it made for it, so nexus replays the first answer instead of
 * applying the action twice.
 */
export function mutatingPost(body: unknown, idempotencyKey = newIdempotencyKey()): RequestInit {
  return mutating("POST", body, idempotencyKey);
}

export function mutatingPut(body: unknown, idempotencyKey = newIdempotencyKey()): RequestInit {
  return mutating("PUT", body, idempotencyKey);
}

function mutating(method: "POST" | "PUT", body: unknown, idempotencyKey: string): RequestInit {
  return {
    method,
    body: JSON.stringify(body),
    headers: { "Idempotency-Key": idempotencyKey },
  };
}

export function put(body: unknown): RequestInit {
  return { method: "PUT", body: JSON.stringify(body) };
}
